/**
 * LandingPage — public marketing surface for ELPM Studio.
 *
 * Lives at `/` for unauthenticated visitors (signed-in users
 * auto-redirect to `/studio` via LandingRoute in App.js).
 *
 * Design philosophy — "hybrid" per the user's iter-170 brief:
 *   - Bold dark hero that mirrors the DAW's signature aesthetic
 *     (rose / fuchsia / cyan gradients on charcoal) so new visitors
 *     pre-see the look of the product.
 *   - Lighter feature + comparison + pricing sections so the page
 *     reads like a SaaS marketing site, not a screenshot of the app.
 *   - Single high-contrast CTA strategy: "Open Studio" gradient pill
 *     repeated at hero / mid-page / end so visitors can convert
 *     wherever they stop reading.
 *
 * iter-170 — first version of the marketing page.
 */
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Layers3, Mic2, Music4, Sparkles, Cpu, Wand2, Check, X, ArrowRight, Github,
  Twitter, Heart, AudioLines, Radio, Headphones,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import LandingDemoPlayer from "@/components/LandingDemoPlayer";
import ElpmLogo from "@/components/ElpmLogo";

const FEATURES = [
  {
    icon: AudioLines,
    title: "64-channel recording",
    body: "Lay down up to 64 simultaneous tracks straight from your mic. Stream-to-disk, never out of memory, per-channel dBFS metering.",
    gradient: "from-rose-400 to-fuchsia-400",
  },
  {
    icon: Layers3,
    title: "12-stem extraction",
    body: "Drop any song in, pull vocals, drums, bass, guitar, piano, and more onto separate channels in under 90 seconds. Powered by fal.ai Demucs.",
    gradient: "from-amber-400 to-orange-400",
  },
  {
    icon: Mic2,
    title: "AI voice cloning",
    body: "Train a model of your own voice from 30 seconds of audio. Then make any lyric sing in your voice via ElevenLabs IVC.",
    gradient: "from-emerald-400 to-cyan-400",
  },
  {
    icon: Music4,
    title: "AI song generation",
    body: "Generate full songs from a prompt — pick a genre, write lyrics, hit Generate. Custom-voice cloning, reference recording, stems on demand.",
    gradient: "from-violet-400 to-fuchsia-400",
  },
  {
    icon: Cpu,
    title: "WAM v2 plugins",
    body: "12 browser-native plugins out of the box, including SSL Buss Comp, Pultec EQ, and Vintage Tape — the Waves-alternative starter pack.",
    gradient: "from-sky-400 to-indigo-400",
  },
  {
    icon: Wand2,
    title: "AI mastering chain",
    body: "Auto-master your tracks with a one-click signal chain tuned for streaming loudness targets. Bake in tape warmth, glue compression, and EQ shaping.",
    gradient: "from-pink-400 to-rose-400",
  },
];

const COMPARISON_ROWS = [
  { label: "Runs in browser, no install", elpm: true, waves: false, garage: false, ableton: false },
  { label: "Free unlimited recording", elpm: true, waves: false, garage: true, ableton: "trial" },
  { label: "64 simultaneous tracks", elpm: true, waves: true, garage: false, ableton: true },
  { label: "AI song generation", elpm: true, waves: false, garage: false, ableton: false },
  { label: "AI voice cloning", elpm: true, waves: false, garage: false, ableton: false },
  { label: "12-stem extraction", elpm: true, waves: false, garage: false, ableton: false },
  { label: "Real-time WAM plugins", elpm: true, waves: true, garage: true, ableton: true },
  { label: "iLok / dongle license", elpm: false, waves: true, garage: false, ableton: false },
];

const PRICING = [
  {
    name: "Free",
    price: "$0",
    cadence: "forever",
    features: [
      "64-channel recording",
      "12-stem extraction (uses your fal.ai key)",
      "AI song generation",
      "6 free WAM plugins",
      "AI voice cloning (your ElevenLabs key)",
      "Unlimited project saves",
    ],
    cta: "Open Studio",
    href: "/studio",
    highlight: false,
  },
  {
    name: "Pro",
    price: "$9",
    cadence: "/ month",
    features: [
      "Everything in Free",
      "6 Pro WAM plugins (Sidechain, Multiband, Phaser…)",
      "AI mastering chain",
      "Priority generation queue",
      "Public song sharing",
      "Owner dashboard analytics",
    ],
    cta: "Start Pro",
    href: "/studio",
    highlight: true,
  },
];

const FAQ = [
  {
    q: "Do I need to install anything?",
    a: "No. ELPM Studio runs entirely in the browser. Bookmark elpm-studio.com and your projects, plugins, and recordings stay with your account.",
  },
  {
    q: "Can I import my own audio?",
    a: "Yes. Drag any MP3 / WAV / M4A onto a channel and the studio decodes it client-side. Recordings happen at native device sample rate (no resampling artifacts).",
  },
  {
    q: "Is my audio private?",
    a: "Recordings live in your account and aren't public unless you explicitly share a song. Stem extraction passes through fal.ai using your own API key.",
  },
  {
    q: "Can I use my Waves plugins?",
    a: "Native Waves VSTs don't run in browsers (that's a browser security limit, not us). But we ship 3 free Waves-alternative plugins out of the box — SSL Buss Comp, Pultec EQ, and Vintage Tape — plus 9 more across modulation, dynamics, and filtering.",
  },
  {
    q: "What's the recording limit?",
    a: "Per take: 60 minutes. Per session: 64 channels × 60 minutes. Total projects: unlimited.",
  },
  {
    q: "Who's behind this?",
    a: "ELPM Studio is built by indie devs who got tired of paying for plugin licenses and waiting for DAWs to launch. Made with Emergent.",
  },
];

function GradientBlob({ className }) {
  return (
    <div className={`pointer-events-none absolute blur-[140px] opacity-40 ${className}`} aria-hidden />
  );
}

function YesNo({ value }) {
  if (value === true) return <Check className="h-4 w-4 text-emerald-400" />;
  if (value === false) return <X className="h-4 w-4 text-rose-400/60" />;
  if (value === "trial") return <span className="font-mono text-[10px] uppercase tracking-wider text-amber-300">trial</span>;
  return <span className="text-white/40">—</span>;
}

export default function LandingPage() {
  return (
    <div data-testid="landing-page" className="relative min-h-screen overflow-x-hidden bg-zinc-950 text-white">
      {/* ── HERO ──────────────────────────────────────────────────── */}
      <header className="relative isolate overflow-hidden border-b border-white/[0.06]">
        <GradientBlob className="-top-32 -left-32 h-96 w-96 rounded-full bg-rose-500" />
        <GradientBlob className="-top-20 right-0 h-80 w-80 rounded-full bg-fuchsia-500" />
        <GradientBlob className="bottom-0 left-1/3 h-72 w-72 rounded-full bg-cyan-500" />
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-5 sm:px-8">
          <Link to="/" data-testid="landing-logo" className="flex items-center" aria-label="ELPM Studio home">
            <ElpmLogo className="h-10" testId="landing-logo-img" />
          </Link>
          <div className="hidden gap-6 sm:flex">
            <a href="#features" data-testid="nav-features" className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/55 transition hover:text-white">Features</a>
            <a href="#comparison" data-testid="nav-comparison" className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/55 transition hover:text-white">Compare</a>
            <a href="#pricing" data-testid="nav-pricing" className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/55 transition hover:text-white">Pricing</a>
            <a href="#faq" data-testid="nav-faq" className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/55 transition hover:text-white">FAQ</a>
          </div>
          <Link to="/studio">
            <Button data-testid="nav-open-studio" className="rounded-full bg-white px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-black hover:bg-white/90">
              Open Studio <ArrowRight className="ml-1 h-3 w-3" />
            </Button>
          </Link>
        </nav>

        <div className="mx-auto max-w-7xl px-5 pb-24 pt-16 sm:px-8 sm:pt-24">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <p data-testid="hero-eyebrow" className="mb-4 inline-flex items-center gap-2 rounded-full border border-fuchsia-400/30 bg-fuchsia-500/[0.10] px-3 py-1 font-mono text-[10px] uppercase tracking-[0.22em] text-fuchsia-200">
              <Radio className="h-3 w-3" />
              ELPM Studio · Gospel music production
            </p>
            <h1 data-testid="hero-title" className="font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              Make a song from scratch
              <br />
              <span className="bg-gradient-to-r from-rose-400 via-fuchsia-400 to-cyan-400 bg-clip-text text-transparent">
                in 60 seconds.
              </span>
            </h1>
            <p data-testid="hero-subtitle" className="mt-5 max-w-xl text-base leading-7 text-white/65 sm:text-lg sm:leading-8">
              A pro-grade 64-channel DAW that runs in your browser. Record vocals, generate full songs with Make Music, clone your voice, extract stems, and ship — no install, no Waves license, no limits.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link to="/studio">
                <Button data-testid="hero-cta-open" size="lg" className="rounded-2xl bg-gradient-to-r from-rose-500 via-fuchsia-500 to-cyan-500 px-5 text-base font-semibold text-white shadow-[0_0_32px_rgba(232,121,249,0.5)] hover:opacity-95">
                  Open Studio · Free <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              {/* iter-171 — Self-contained synth demo player. Renders a
                  4-bar electronic loop client-side via OfflineAudioContext
                  so visitors can HEAR ELPM before clicking through. */}
              <LandingDemoPlayer />
            </div>
            <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">
              No credit card · runs in Chrome, Edge, Brave, Arc · demo synthesized live in your browser
            </p>
          </motion.div>
        </div>
      </header>

      {/* ── FEATURE GRID ──────────────────────────────────────────── */}
      <section id="features" data-testid="section-features" className="border-b border-white/[0.06] bg-zinc-950">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
          <div className="mb-12 max-w-2xl">
            <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-300/70">What&apos;s inside</p>
            <h2 className="font-display text-3xl font-bold leading-[1.1] tracking-tight sm:text-4xl">
              Everything a real studio has — built for the browser.
            </h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.title}
                  data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5 }}
                  className="group relative overflow-hidden rounded-3xl border border-white/[0.06] bg-white/[0.02] p-6 transition hover:border-white/[0.12] hover:bg-white/[0.04]"
                >
                  <div className={`mb-4 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br ${f.gradient} text-white`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mb-1.5 font-display text-lg font-semibold tracking-tight">{f.title}</h3>
                  <p className="text-[13px] leading-6 text-white/55">{f.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── COMPARISON ────────────────────────────────────────────── */}
      <section id="comparison" data-testid="section-comparison" className="border-b border-white/[0.06] bg-zinc-950/95">
        <div className="mx-auto max-w-5xl px-5 py-20 sm:px-8 sm:py-28">
          <div className="mb-12 max-w-2xl">
            <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.22em] text-fuchsia-300/70">How it stacks up</p>
            <h2 className="font-display text-3xl font-bold leading-[1.1] tracking-tight sm:text-4xl">
              ELPM Studio vs. the alternatives.
            </h2>
            <p className="mt-3 max-w-xl text-[14px] leading-7 text-white/55">
              Most desktop DAWs cost hundreds and need a license server running. Browser tools are usually toys. ELPM sits in the sweet spot.
            </p>
          </div>
          <div className="overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.02]">
            <table className="w-full text-sm" data-testid="comparison-table">
              <thead>
                <tr className="border-b border-white/[0.08] bg-white/[0.04]">
                  <th className="px-4 py-3 text-left font-mono text-[10px] font-medium uppercase tracking-wider text-white/45">Feature</th>
                  <th className="px-4 py-3 text-center font-display text-[12px] font-semibold tracking-tight">
                    <span className="bg-gradient-to-r from-rose-400 to-fuchsia-400 bg-clip-text text-transparent">ELPM Studio</span>
                  </th>
                  <th className="px-4 py-3 text-center font-display text-[12px] font-semibold text-white/70">Waves Studio</th>
                  <th className="px-4 py-3 text-center font-display text-[12px] font-semibold text-white/70">GarageBand</th>
                  <th className="px-4 py-3 text-center font-display text-[12px] font-semibold text-white/70">Ableton Trial</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS.map((r) => (
                  <tr key={r.label} className="border-b border-white/[0.04] last:border-b-0 transition hover:bg-white/[0.02]">
                    <td className="px-4 py-3 text-[13px] text-white/75">{r.label}</td>
                    <td className="px-4 py-3 text-center"><YesNo value={r.elpm} /></td>
                    <td className="px-4 py-3 text-center"><YesNo value={r.waves} /></td>
                    <td className="px-4 py-3 text-center"><YesNo value={r.garage} /></td>
                    <td className="px-4 py-3 text-center"><YesNo value={r.ableton} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── PRICING ───────────────────────────────────────────────── */}
      <section id="pricing" data-testid="section-pricing" className="border-b border-white/[0.06] bg-zinc-950">
        <div className="mx-auto max-w-5xl px-5 py-20 sm:px-8 sm:py-28">
          <div className="mb-12 max-w-2xl">
            <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-300/70">Pricing</p>
            <h2 className="font-display text-3xl font-bold leading-[1.1] tracking-tight sm:text-4xl">
              Free to start. Pro when you need more.
            </h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {PRICING.map((tier) => (
              <motion.div
                key={tier.name}
                data-testid={`pricing-tier-${tier.name.toLowerCase()}`}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5 }}
                className={`relative rounded-3xl border p-6 sm:p-8 ${tier.highlight
                    ? "border-fuchsia-400/40 bg-gradient-to-br from-fuchsia-500/[0.10] via-rose-500/[0.06] to-cyan-500/[0.08] shadow-[0_30px_60px_-30px_rgba(232,121,249,0.4)]"
                    : "border-white/[0.08] bg-white/[0.02]"
                  }`}
              >
                {tier.highlight && (
                  <span className="absolute right-4 top-4 rounded-full bg-gradient-to-r from-fuchsia-500 to-rose-500 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em] text-white">
                    Most popular
                  </span>
                )}
                <h3 className="font-display text-2xl font-bold tracking-tight">{tier.name}</h3>
                <p className="mt-1 flex items-baseline gap-1.5">
                  <span className="font-display text-4xl font-bold">{tier.price}</span>
                  <span className="text-sm text-white/45">{tier.cadence}</span>
                </p>
                <ul className="mt-6 space-y-2.5">
                  {tier.features.map((feat) => (
                    <li key={feat} className="flex items-start gap-2 text-[13px] text-white/70">
                      <Check className={`mt-0.5 h-3.5 w-3.5 flex-shrink-0 ${tier.highlight ? "text-fuchsia-300" : "text-emerald-400"}`} />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
                <Link to={tier.href} className="mt-7 block">
                  <Button
                    data-testid={`pricing-cta-${tier.name.toLowerCase()}`}
                    className={`w-full rounded-2xl ${tier.highlight
                        ? "bg-gradient-to-r from-fuchsia-500 to-rose-500 text-white shadow-[0_0_20px_rgba(232,121,249,0.4)] hover:opacity-95"
                        : "bg-white text-black hover:bg-white/90"
                      }`}
                  >
                    {tier.cta} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────────── */}
      <section id="faq" data-testid="section-faq" className="border-b border-white/[0.06] bg-zinc-950/95">
        <div className="mx-auto max-w-3xl px-5 py-20 sm:px-8 sm:py-28">
          <div className="mb-12 max-w-2xl">
            <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.22em] text-sky-300/70">FAQ</p>
            <h2 className="font-display text-3xl font-bold leading-[1.1] tracking-tight sm:text-4xl">
              Frequently asked.
            </h2>
          </div>
          <div className="space-y-3">
            {FAQ.map((entry) => (
              <details
                key={entry.q}
                data-testid={`faq-${entry.q.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 24)}`}
                className="group rounded-2xl border border-white/[0.06] bg-white/[0.02] px-5 py-4 transition open:border-white/[0.12] open:bg-white/[0.04]"
              >
                <summary className="flex cursor-pointer items-center justify-between gap-4 font-display text-[15px] font-semibold tracking-tight text-white">
                  {entry.q}
                  <span className="font-mono text-xs text-white/35 transition group-open:rotate-45">+</span>
                </summary>
                <p className="mt-3 text-[13px] leading-7 text-white/60">{entry.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ─────────────────────────────────────────────── */}
      <section className="relative isolate overflow-hidden border-b border-white/[0.06] bg-zinc-950">
        <GradientBlob className="top-0 left-1/4 h-72 w-72 rounded-full bg-fuchsia-500" />
        <GradientBlob className="bottom-0 right-1/4 h-72 w-72 rounded-full bg-cyan-500" />
        <div className="mx-auto max-w-3xl px-5 py-24 text-center sm:px-8">
          <Headphones className="mx-auto mb-5 h-10 w-10 text-fuchsia-300" />
          <h2 className="font-display text-3xl font-bold leading-tight tracking-tight sm:text-4xl">
            Your next song is one click away.
          </h2>
          <p className="mt-3 text-base text-white/55 sm:text-lg">
            Free forever. No install. No license servers. Just open and ship.
          </p>
          <Link to="/studio" className="mt-7 inline-block">
            <Button data-testid="final-cta-open" size="lg" className="rounded-2xl bg-gradient-to-r from-rose-500 via-fuchsia-500 to-cyan-500 px-6 text-base font-semibold text-white shadow-[0_0_36px_rgba(232,121,249,0.55)] hover:opacity-95">
              Open Studio · Free <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* ── FOOTER ────────────────────────────────────────────────── */}
      <footer data-testid="landing-footer" className="bg-zinc-950">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-5 py-8 sm:px-8">
          <Link to="/" className="flex items-center" aria-label="ELPM Studio home">
            <ElpmLogo className="h-8" testId="landing-footer-logo" />
          </Link>
          <div className="flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">
            <a href="https://github.com/elpm-studio" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 transition hover:text-white"><Github className="h-3 w-3" />GitHub</a>
            <a href="https://twitter.com/elpmstudio" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 transition hover:text-white"><Twitter className="h-3 w-3" />Twitter</a>
            <Link to="/status" data-testid="footer-status" className="transition hover:text-white">Status</Link>
            <Link to="/settings" data-testid="footer-settings" className="transition hover:text-white">Settings</Link>
          </div>
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/35">
            <Heart className="-mt-0.5 mr-1 inline h-3 w-3 text-rose-400/70" />
            ELPM Studio · Apostle Moore
          </p>
        </div>
      </footer>
    </div>
  );
}
